import { DataEnum } from "models/groupBy";

import { MlFeature, MlTarget } from "components/ml/types";

export enum ErrorTypes {
  InvalidData = "INVALID_DATA_ERROR",
  SingularValue = "SINGLE_VALUE_ERROR",
  Internal = "INTERNAL_ERROR",
  MissingField = "MISSING_FIELD",
  Value = "VALUE_ERROR",
  External = "EXTERNAL_SERVICE_ERROR"
}

export const LOW_WELL_COUNT_WARNING_THRESHOLD = 100;

/** Default ML target feature used when no target is saved in a template or on reset. */
export const DEFAULT_TARGET: MlTarget = {
  Property: "Performance.Oil.EUR.fcst",
  NiceName: "Oil + Cond: EUR",
  Unit: "Mbbl",
  AllUnits: { 0: "Mbbl", 1: "Mbbl", 2: "Mbbl" },
  CanBeZero: true
};

/** Default input features for the ML feature selector (without geo features). */
export const DEFAULT_FEATURES_LIST: Omit<MlFeature, "unit">[] = [
  {
    Property: "Well_Design.HzLength_m",
    NiceName: "Hz Length",
    CanBeZero: true,
    category: "Well Design",
    checked: false,
    Monotonic: 1,
    dataType: DataEnum.Number
  },
  {
    Property: "Frac_Data.ProppantIntensity_tm",
    NiceName: "Proppant Intensity",
    CanBeZero: true,
    category: "Frac Data",
    checked: false,
    Monotonic: 1,
    dataType: DataEnum.Number
  },
  {
    Property: "Frac_Data.TotalPropConcentration_kgm3",
    NiceName: "Total Prop Concentration",
    CanBeZero: true,
    category: "Frac Data",
    checked: false,
    Monotonic: -1,
    dataType: DataEnum.Number
  },
  {
    Property: "Performance.CGR.TwelveMProdTimeCum.fcst",
    NiceName: "12M ProdTime Cum + Forecast",
    CanBeZero: true,
    category: "Performance",
    checked: false,
    Monotonic: 1,
    product: "(O+C)GR",
    dataType: DataEnum.Number
  },
  {
    Property: "Well_Design.Interfering_XdaGross_ha",
    NiceName: "Gross XDA Unconstrained",
    CanBeZero: true,
    category: "Well Spacing",
    checked: false,
    Monotonic: 1,
    dataType: DataEnum.Number
  },
  {
    Property: "Well_Design.DistanceWeightedWellCountInPlay",
    NiceName: "Distance Weighted Well Count in Play",
    CanBeZero: true,
    category: "Well Count",
    checked: false,
    Monotonic: -1,
    dataType: DataEnum.Number
  },
  {
    Property: "TuringParentChild.ParentCumHoursDWwithinPlay",
    NiceName: "Distance Weighted Parent Cum Hours within Play",
    CanBeZero: true,
    category: "Parent Child",
    checked: false,
    Monotonic: -1,
    dataType: DataEnum.Number
  }
];

/** Additional geo features included when the user has 3D geo model access. */
export const DEFAULT_GEO_FEATURES_LIST: Omit<MlFeature, "unit">[] = [
  {
    Property: "ThreedvSweetSpot.Pressure_Default",
    NiceName: "Pressure",
    CanBeZero: true,
    category: "Sampled 3D Geo",
    checked: false,
    Monotonic: 1,
    dataType: DataEnum.Number
  },
  {
    Property: "ThreedvSweetSpot.Porosity_Default",
    NiceName: "Porosity",
    CanBeZero: true,
    category: "Sampled 3D Geo",
    checked: false,
    Monotonic: 1,
    dataType: DataEnum.Number
  },
  {
    Property: "ThreedvSweetSpot.Sw_Default",
    NiceName: "Sw",
    CanBeZero: true,
    category: "Sampled 3D Geo",
    checked: false,
    Monotonic: -1,
    dataType: DataEnum.Number
  }
];
